import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import LeadPicker from "./LeadPicker";

const EMPTY = {
  activity_type: "CALL",
  title: "", description: "", outcome: "",
  next_action: "", next_follow_up_date: "", order_amount: "", order_date: "",
  sample_details: "",
  status: "Open",
};

export default function AddActivityModal({ open, onOpenChange, leadId, defaultType, editing, onCreated, onDeleted, calendarMode = false }) {
  const [enums, setEnums] = useState({ activity_types: [], outcomes: [], activity_statuses: ["Open", "Completed"] });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pickedLead, setPickedLead] = useState(null);

  const isEdit = Boolean(editing);
  // When opened from Lead Detail: leadId is provided -> hide picker
  // When opened from Activities page: leadId not provided -> show required picker
  const needsLeadPicker = !isEdit && !leadId;
  const effectiveLeadId = leadId || pickedLead?.id;

  useEffect(() => {
    api.get("/meta/enums").then(({ data }) => setEnums(data));
  }, []);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        activity_type: editing.activity_type || "CALL",
        title: editing.title || "",
        description: editing.description || "",
        outcome: editing.outcome || "",
        next_action: editing.next_action || "",
        next_follow_up_date: editing.next_follow_up_date ? editing.next_follow_up_date.slice(0, 16) : "",
        order_amount: editing.order_amount ?? "",
        order_date: editing.order_date ? editing.order_date.slice(0, 10) : "",
        sample_details: editing.sample_details || "",
        status: editing.status || "Open",
      });
    } else {
      setForm({ ...EMPTY, activity_type: defaultType || "CALL" });
      setPickedLead(null);
    }
  }, [open, editing, defaultType]);

  const isOrder = form.activity_type === "ORDER";
  const orderAmountInvalid = isOrder && (
    form.order_amount === "" || form.order_amount === null ||
    isNaN(parseFloat(form.order_amount)) || parseFloat(form.order_amount) <= 0
  );

  const submit = async () => {
    if (needsLeadPicker && !pickedLead?.id) {
      toast.error("Please select a valid existing lead before saving this activity.");
      return;
    }
    if (orderAmountInvalid) {
      toast.error("Order Amount (€) is required for ORDER activities and must be greater than zero.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.order_amount === "" || payload.order_amount === null) delete payload.order_amount;
      else payload.order_amount = parseFloat(payload.order_amount);
      if (!payload.order_date) delete payload.order_date;
      if (!payload.next_follow_up_date) payload.next_follow_up_date = null;
      if (!payload.outcome) delete payload.outcome;
      if (!payload.title?.trim()) delete payload.title;

      if (isEdit) {
        await api.put(`/activities/${editing.id}`, payload);
        toast.success("Activity updated");
      } else {
        await api.post(`/leads/${effectiveLeadId}/activities`, payload);
        toast.success("Activity logged");
      }
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save activity");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this activity?")) return;
    setDeleting(true);
    try {
      await api.delete(`/activities/${editing.id}`);
      toast.success("Activity deleted");
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="activity-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? "Edit Activity" : "Log Activity"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? "Edit an existing activity record." : "Log a new activity for a lead."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {needsLeadPicker && (
            <div className="sm:col-span-2">
              <Label>
                Lead <span className="text-red-500">*</span>
              </Label>
              <LeadPicker value={pickedLead} onChange={setPickedLead} />
              <div className="text-[11px] text-gray-500 mt-1">
                Select an existing lead from the CRM Company list. Start typing the company or contact name, then choose one of the suggested results.
              </div>
              {!pickedLead && (
                <div data-testid="lead-picker-validation"
                  className="text-[11px] text-red-500 mt-1">
                  Please select a valid existing lead before saving this activity.
                </div>
              )}
            </div>
          )}
          <div>
            <Label>Type</Label>
            <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
              <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {enums.activity_types.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Outcome</Label>
            <Select value={form.outcome || "__none"} onValueChange={(v) => setForm({ ...form, outcome: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="activity-outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {enums.outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Title (optional — auto-generated)</Label>
            <Input
              data-testid="activity-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Leave empty to auto-generate"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Textarea
              data-testid="activity-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Conversation notes"
            />
          </div>
          {isOrder && (
            <>
              <div>
                <Label>
                  Order Amount (€) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="activity-order-amount"
                  value={form.order_amount}
                  onChange={(e) => setForm({ ...form, order_amount: e.target.value })}
                  placeholder="e.g. 1200.50"
                />
                {orderAmountInvalid && (
                  <div data-testid="order-amount-validation"
                    className="text-[11px] text-red-500 mt-1">
                    Required for ORDER. Must be a positive number (net of VAT).
                  </div>
                )}
              </div>
              <div>
                <Label>Order Date</Label>
                <input
                  type="date"
                  data-testid="activity-order-date"
                  value={form.order_date}
                  onChange={(e) => setForm({ ...form, order_date: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-10"
                />
                <div className="text-[11px] text-gray-400 mt-1">
                  Optional. Defaults to today on save.
                </div>
              </div>
            </>
          )}
          {form.activity_type === "SAMPLE" && (
            <div className="sm:col-span-2">
              <Label>Sample Details</Label>
              <Input value={form.sample_details}
                onChange={(e) => setForm({ ...form, sample_details: e.target.value })}
                placeholder="e.g. 50g Big Flower Gorilla" />
            </div>
          )}
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="activity-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Next Follow-up</Label>
            <input
              type="datetime-local"
              data-testid="activity-followup-input"
              value={form.next_follow_up_date}
              onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-10"
            />
            {calendarMode && !form.next_follow_up_date && (
              <div data-testid="calendar-no-date-helper"
                className="text-[11px] text-amber-700 mt-1 leading-snug">
                Without a follow-up date, this activity will be saved but will not appear in the calendar grid.
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label>Next Action</Label>
            <Input value={form.next_action}
              onChange={(e) => setForm({ ...form, next_action: e.target.value })}
              placeholder="e.g. Send quote" />
          </div>
        </div>
        <DialogFooter className="flex flex-row-reverse sm:flex-row sm:justify-between gap-2">
          {isEdit ? (
            <button onClick={onDelete} disabled={deleting} data-testid="activity-delete-button"
              className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 px-3 py-2 rounded-md hover:bg-red-50">
              <Trash size={14} /> {deleting ? "Deleting..." : "Delete"}
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="activity-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || (needsLeadPicker && !pickedLead?.id) || orderAmountInvalid}
              data-testid="activity-save-button"
              className="bg-[#1A3A21] hover:bg-[#142C19] text-white"
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Activity"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
